console.log('[transform.ts loaded - start]');
import {
  AirtableError, AirtableErrorType, ErrorSeverity
} from './types';
import {
  AirtableRecord,
  CRORecord,
  StainingPlatformRecord,
  ImageScanningRecord,
  ImageAnalysisRecord,
  CompanyRecord
} from './tables';
console.log('[transform.ts loaded - after imports from ./types and ./tables]');
import { getDynamicTableSchema } from './metadata-api';
console.log('[transform.ts loaded - after import metadata-api]');
import { AIRTABLE_TABLES } from './config';
console.log('[transform.ts loaded - after import config]');

/**
 * Airtable Data Transformation Utilities
 * 
 * Provides utilities for transforming data between application models and Airtable records
 * Handles serialization, deserialization, validation, and special field types
 */

/**
 * Application Models
 * These represent the data as used within the application
 */

console.log('[transform.ts] before BaseModel');
export interface BaseModel {
  id: string;
  createdAt?: Date;
}
console.log('[transform.ts] after BaseModel');

// CRO Model
console.log('[transform.ts] before CRO');
export interface CRO extends BaseModel {
  name: string;
  description?: string;
  location?: string;
  website?: string;
  contact?: string;
  stainingPlatforms?: string[]; // IDs of related staining platforms
  imageScanning?: string[]; // IDs of related image scanning platforms
  imageAnalysis?: string[]; // IDs of related image analysis software
  certifications?: string[];
  experienceLevel?: 'Low' | 'Medium' | 'High';
  notes?: string;
}
console.log('[transform.ts] after CRO');

// Staining Platform Model
console.log('[transform.ts] before StainingPlatform');
export interface StainingPlatform extends BaseModel {
  name: string;
  manufacturer: string;
  description?: string;
  sampleTypes?: string[];
  antibodyProtocols?: string[];
  throughputLevel?: 'Low' | 'Medium' | 'High';
  companies?: string[]; // IDs of related companies
  notes?: string;
}
console.log('[transform.ts] after StainingPlatform');

// Image Scanning Model
console.log('[transform.ts] before ImageScanning');
export interface ImageScanning extends BaseModel {
  name: string;
  technology: string;
  description?: string;
  resolution?: string;
  supportedSlides?: string[];
  throughputLevel?: 'Low' | 'Medium' | 'High';
  companies?: string[]; // IDs of related companies
  notes?: string;
}
console.log('[transform.ts] after ImageScanning');

// Image Analysis Model
console.log('[transform.ts] before ImageAnalysis');
export interface ImageAnalysis extends BaseModel {
  name: string;
  developer?: string;
  analysisType: string[];
  description?: string;
  supportedFormats?: string[];
  isCloudBased?: boolean;
  hasMachineLearning?: boolean;
  hasBatchProcessing?: boolean;
  companies?: string[]; // IDs of related companies
  notes?: string;
}
console.log('[transform.ts] after ImageAnalysis');

// Company Model
console.log('[transform.ts] before Company');
export interface Company extends BaseModel {
  name: string;
  category: string[];
  description?: string;
  location?: string;
  website?: string;
  contact?: string;
  stainingPlatforms?: string[]; // IDs of related staining platforms
  imageScanning?: string[]; // IDs of related image scanning platforms
  imageAnalysis?: string[]; // IDs of related image analysis software
  notes?: string;
}
console.log('[transform.ts] after Company');

// Platforms Model
console.log('[transform.ts] before Platforms');
export interface Platforms extends BaseModel {
  name: string;
  description?: string;
  manufacturer?: string;
  technology?: string;
  companies?: string[];
}
console.log('[transform.ts] after Platforms');

/**
 * Generic model type mapping based on table name
 */
console.log('[transform.ts] before TableModelType');
export type TableModelType<T extends string = string> =
  T extends 'PROVIDERS' ? Company :
  T extends 'PRODUCTS' ? BaseModel :
  T extends 'PROVIDER_SERVICES' ? BaseModel :
  T extends 'CRO_SERVICES' ? BaseModel :
  T extends 'CROS' ? BaseModel :
  T extends 'TECHNOLOGY' ? BaseModel :
  T extends 'PLATFORMS' ? Platforms :
  BaseModel;
console.log('[transform.ts] after TableModelType');

/**
 * Validation error for data transformations
 */
console.log('[transform.ts] before ValidationError');
export class ValidationError extends AirtableError {
  constructor(message: string, context?: Record<string, any>) {
    super(
      message,
      AirtableErrorType.VALIDATION,
      ErrorSeverity.WARNING,
      undefined,
      context
    );
    this.name = 'ValidationError';
  }
}
console.log('[transform.ts] after ValidationError');

/**
 * Generic field transformers
 */

/**
 * Format Date object to ISO string for Airtable
 */
export function formatDate(date?: Date): string | undefined {
  if (date && typeof date.toISOString === 'function') {
    return date.toISOString();
  } else {
    return '';
  }
}

/**
 * Convert boolean values consistently
 */
export function parseBoolean(value: any): boolean | undefined {
  console.log('[parseBoolean] called with:', value, 'type:', typeof value, 'stack:', new Error().stack);
  if (typeof value === 'string') {
    try {
      return ['true', 'yes', '1'].includes(value.toLowerCase());
    } catch (err) {
      console.log('[parseBoolean] Error with value:', value, err, 'stack:', new Error().stack);
      return undefined;
    }
  }
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (value === undefined || value === null) {
    console.log('[parseBoolean] Received undefined/null:', value, 'stack:', new Error().stack);
    return undefined;
  }
  console.log('[parseBoolean] Unexpected type:', typeof value, value, 'stack:', new Error().stack);
  return undefined;
}

/**
 * Ensure array values
 */
export function parseArray<T>(value: T | T[] | undefined): T[] | undefined {
  if (value === undefined || value === null) return undefined;
  return Array.isArray(value) ? value : [value];
}

/**
 * Create URL from string with validation
 */
export function parseUrl(urlString?: string): string | undefined {
  if (!urlString) return undefined;
  try {
    const url = new URL(urlString);
    return url && typeof url.toString === 'function' ? url.toString() : String(url);
  } catch (error) {
    // Return the original string if it's not a valid URL
    return urlString;
  }
}

/**
 * Validate email format
 */
export function validateEmail(email?: string): string | undefined {
  if (!email) return undefined;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new ValidationError(`Invalid email format: ${email}`);
  }
  return email;
}

/**
 * Serializers: Convert application models to Airtable record format
 */

/**
 * Serialize CRO model to Airtable record fields
 */
export function serializeCRO(cro: Partial<CRO>): CRORecord['fields'] {
  return {
    Name: cro.name!,
    Description: cro.description,
    Location: cro.location,
    Website: cro.website,
    Contact: cro.contact,
    StainingPlatforms: cro.stainingPlatforms,
    ImageScanning: cro.imageScanning,
    ImageAnalysis: cro.imageAnalysis,
    Certifications: cro.certifications,
    ExperienceLevel: cro.experienceLevel,
    Notes: cro.notes
  };
}

/**
 * Serialize StainingPlatform model to Airtable record fields
 */
export function serializeStainingPlatform(platform: Partial<StainingPlatform>): StainingPlatformRecord['fields'] {
  return {
    Name: platform.name!,
    Manufacturer: platform.manufacturer!,
    Description: platform.description,
    SampleTypes: platform.sampleTypes,
    AntibodyProtocols: platform.antibodyProtocols,
    ThroughputLevel: platform.throughputLevel,
    Companies: platform.companies,
    Notes: platform.notes
  };
}

/**
 * Serialize ImageScanning model to Airtable record fields
 */
export function serializeImageScanning(scanning: Partial<ImageScanning>): ImageScanningRecord['fields'] {
  return {
    Name: scanning.name!,
    Technology: scanning.technology!,
    Description: scanning.description,
    Resolution: scanning.resolution,
    SupportedSlides: scanning.supportedSlides,
    ThroughputLevel: scanning.throughputLevel,
    Companies: scanning.companies,
    Notes: scanning.notes
  };
}

/**
 * Serialize ImageAnalysis model to Airtable record fields
 */
export function serializeImageAnalysis(analysis: Partial<ImageAnalysis>): ImageAnalysisRecord['fields'] {
  return {
    Name: analysis.name!,
    Developer: analysis.developer,
    AnalysisType: analysis.analysisType || [],
    Description: analysis.description,
    SupportedFormats: analysis.supportedFormats,
    CloudBased: analysis.isCloudBased,
    MachineLearning: analysis.hasMachineLearning,
    BatchProcessing: analysis.hasBatchProcessing,
    Companies: analysis.companies,
    Notes: analysis.notes
  };
}

/**
 * Serialize Company model to Airtable record fields
 */
export function serializeCompany(company: Partial<Company>): CompanyRecord['fields'] {
  return {
    Name: company.name!,
    Category: company.category || [],
    Description: company.description,
    Location: company.location,
    Website: company.website,
    Contact: company.contact,
    StainingPlatforms: company.stainingPlatforms,
    ImageScanning: company.imageScanning,
    ImageAnalysis: company.imageAnalysis,
    Notes: company.notes
  };
}

/**
 * Serialize Platforms model to Airtable record fields
 */
export function serializePlatforms(platform: Partial<Platforms>): Record<string, any> {
  return {
    Name: platform.name!,
    Description: platform.description,
    Manufacturer: platform.manufacturer,
    Technology: platform.technology,
    Companies: platform.companies,
  };
}

/**
 * Generic serializer function based on table name
 */
export function serialize<T extends string>(
  tableName: T, 
  model: Partial<TableModelType<T>>
): Record<string, any> {
  switch(tableName) {
    case 'PROVIDERS':
      return serializeCompany(model as Partial<Company>);
    case 'PLATFORMS':
      return serializePlatforms(model as Partial<Platforms>);
    default:
      throw new ValidationError(`Unsupported table name: ${tableName}`);
  }
}

/**
 * Deserializers: Convert Airtable records to application models
 */

/**
 * Deserialize Airtable CRO record to application model
 */
export function deserializeCRO(record: CRORecord): CRO {
  console.log('[deserializeCRO] called with:', record, 'stack:', new Error().stack);
  if (!record || !record.fields) {
    console.log('[deserializeCRO] Invalid record:', record, 'stack:', new Error().stack);
    return {
      id: record?.id || '',
      name: '',
    };
  }
  return {
    id: record.id,
    name: record.fields.Name,
    description: record.fields.Description,
    location: record.fields.Location,
    website: record.fields.Website,
    contact: record.fields.Contact,
    stainingPlatforms: record.fields.StainingPlatforms,
    imageScanning: record.fields.ImageScanning,
    imageAnalysis: record.fields.ImageAnalysis,
    certifications: record.fields.Certifications,
    experienceLevel: record.fields.ExperienceLevel,
    notes: record.fields.Notes
  };
}

/**
 * Deserialize Airtable StainingPlatform record to application model
 */
export function deserializeStainingPlatform(record: StainingPlatformRecord): StainingPlatform {
  console.log('[deserializeStainingPlatform] called with:', record, 'stack:', new Error().stack);
  if (!record || !record.fields) {
    console.log('[deserializeStainingPlatform] Invalid record:', record, 'stack:', new Error().stack);
    return {
      id: record?.id || '',
      name: '',
      manufacturer: '',
    };
  }
  return {
    id: record.id,
    name: record.fields.Name,
    manufacturer: record.fields.Manufacturer!,
    description: record.fields.Description,
    sampleTypes: record.fields.SampleTypes,
    antibodyProtocols: record.fields.AntibodyProtocols,
    throughputLevel: record.fields.ThroughputLevel,
    companies: record.fields.Companies,
    notes: record.fields.Notes
  };
}

/**
 * Deserialize Airtable ImageScanning record to application model
 */
export function deserializeImageScanning(record: ImageScanningRecord): ImageScanning {
  console.log('[deserializeImageScanning] called with:', record, 'stack:', new Error().stack);
  if (!record || !record.fields) {
    console.log('[deserializeImageScanning] Invalid record:', record, 'stack:', new Error().stack);
    return {
      id: record?.id || '',
      name: '',
      technology: '',
    };
  }
  return {
    id: record.id,
    name: record.fields.Name,
    technology: record.fields.Technology!,
    description: record.fields.Description,
    resolution: record.fields.Resolution,
    supportedSlides: record.fields.SupportedSlides,
    throughputLevel: record.fields.ThroughputLevel,
    companies: record.fields.Companies,
    notes: record.fields.Notes
  };
}

/**
 * Deserialize Airtable ImageAnalysis record to application model
 */
export function deserializeImageAnalysis(record: ImageAnalysisRecord): ImageAnalysis {
  console.log('[deserializeImageAnalysis] called with:', record, 'stack:', new Error().stack);
  if (!record || !record.fields) {
    console.log('[deserializeImageAnalysis] Invalid record:', record, 'stack:', new Error().stack);
    return {
      id: record?.id || '',
      name: '',
      analysisType: [],
    };
  }
  return {
    id: record.id,
    name: record.fields.Name,
    developer: record.fields.Developer,
    analysisType: record.fields.AnalysisType || [],
    description: record.fields.Description,
    supportedFormats: record.fields.SupportedFormats,
    isCloudBased: parseBoolean(record.fields.CloudBased),
    hasMachineLearning: parseBoolean(record.fields.MachineLearning),
    hasBatchProcessing: parseBoolean(record.fields.BatchProcessing),
    companies: record.fields.Companies,
    notes: record.fields.Notes
  };
}

/**
 * Deserialize Airtable Company record to application model
 */
export function deserializeCompany(record: CompanyRecord): Company {
  console.log('[deserializeCompany] called with:', record, 'stack:', new Error().stack);
  if (!record || !record.fields) {
    console.log('[deserializeCompany] Invalid record:', record, 'stack:', new Error().stack);
    return {
      id: record?.id || '',
      name: '',
      category: [],
    };
  }
  return {
    id: record.id,
    name: record.fields.Name,
    category: record.fields.Category || [],
    description: record.fields.Description,
    location: record.fields.Location,
    website: record.fields.Website,
    contact: record.fields.Contact,
    stainingPlatforms: record.fields.StainingPlatforms,
    imageScanning: record.fields.ImageScanning,
    imageAnalysis: record.fields.ImageAnalysis,
    notes: record.fields.Notes
  };
}

/**
 * Deserialize Airtable Platforms record to application model
 */
export function deserializePlatforms(record: any): Platforms {
  return {
    id: record.id,
    name: record.fields.Name,
    description: record.fields.Description,
    manufacturer: record.fields.Manufacturer,
    technology: record.fields.Technology,
    companies: record.fields.Companies,
    createdAt: record.createdTime ? new Date(record.createdTime) : undefined,
  };
}

/**
 * Generic deserializer function based on table name
 */
export function deserialize(
  tableName: string,
  record: AirtableRecord
): any {
  switch(tableName) {
    case 'PROVIDERS':
      return deserializeCompany(record as CompanyRecord);
    case 'PLATFORMS':
      return deserializePlatforms(record);
    default:
      return record;
  }
}

/**
 * Batch transformation utilities
 */

/**
 * Deserialize multiple records at once
 */
export function deserializeRecords(
  tableName: string,
  records: AirtableRecord[]
): any[] {
  return records.map(record => deserialize(tableName, record));
}

/**
 * Validation utilities
 */

/**
 * Validate a model against requirements before serialization
 * Returns the validated model or throws ValidationError
 */
export function validateModel<T extends string>(
  tableName: T,
  model: Partial<TableModelType<T>>
): Partial<TableModelType<T>> {
  const errors: string[] = [];

  // Common validation for all models
  if ('name' in model && !model.name) {
    errors.push('Name is required');
  }

  // Table-specific validation
  switch(tableName) {
    case 'PROVIDERS':
      if (!('category' in model) || !(model as Partial<Company>).category?.length) {
        errors.push('At least one Category is required for Providers');
      }
      break;
  }

  // Custom field validations
  if ('website' in model && (model as any).website) {
    try {
      parseUrl((model as any).website);
    } catch (error) {
      errors.push('Website must be a valid URL');
    }
  }

  if ('contact' in model && (model as any).contact) {
    try {
      validateEmail((model as any).contact);
    } catch (error) {
      errors.push('Contact should be a valid email address');
    }
  }

  if (errors.length > 0) {
    throw new ValidationError(`Validation failed: ${errors.join(', ')}`);
  }

  return model;
}

/**
 * Special field handling utilities
 */

/**
 * Handle Airtable attachment fields
 */
export interface AirtableAttachment {
  id: string;
  url: string;
  filename: string;
  size: number;
  type: string;
  thumbnails?: {
    small?: { url: string; width: number; height: number };
    large?: { url: string; width: number; height: number };
  };
}

/**
 * Application attachment model
 */
export interface Attachment {
  id: string;
  url: string;
  filename: string;
  size: number;
  type: string;
  thumbnailSmall?: string;
  thumbnailLarge?: string;
}

/**
 * Convert Airtable attachment to application model
 */
export function parseAttachment(attachment: AirtableAttachment): Attachment {
  return {
    id: attachment.id,
    url: attachment.url,
    filename: attachment.filename,
    size: attachment.size,
    type: attachment.type,
    thumbnailSmall: attachment.thumbnails?.small?.url,
    thumbnailLarge: attachment.thumbnails?.large?.url
  };
}

/**
 * Convert array of Airtable attachments to application model
 */
export function parseAttachments(attachments?: AirtableAttachment[]): Attachment[] | undefined {
  if (!attachments || !attachments.length) return undefined;
  return attachments.map(parseAttachment);
}

/**
 * Serialize attachment for Airtable
 * For new attachments, provide URLs that Airtable will download
 */
export function serializeAttachments(attachments?: Attachment[] | string[]): any[] | undefined {
  if (!attachments || !attachments.length) return undefined;
  
  return attachments.map(attachment => {
    if (typeof attachment === 'string') {
      // If it's just a URL string
      return { url: attachment };
    } else {
      // If it's an existing attachment object
      return { url: attachment.url };
    }
  });
}

/**
 * Handle Airtable linked record fields
 * These helpers provide type-safe ways to manage relationships
 */

/**
 * Get array of record IDs for related records
 */
export function getLinkedRecordIds(fields: Record<string, any>, fieldName: string): string[] | undefined {
  const value = fields[fieldName];
  if (!value) return undefined;
  return Array.isArray(value) ? value : [value];
}

/**
 * Get a map of field names to types/options for a table (dynamic)
 */
export async function getDynamicFieldMap(tableName: string) {
  if (typeof window !== 'undefined') {
    throw new Error('getDynamicFieldMap must only be called server-side.');
  }
  // Patch: Use table ID if tableName is a logical key
  const tableId = AIRTABLE_TABLES[tableName]?.id || tableName;
  const schema = await getDynamicTableSchema(tableId);
  const map: Record<string, { type: string; options?: any }> = {};
  for (const field of schema.fields) {
    map[field.name] = { type: field.type, options: field.options };
  }
  return map;
}

/**
 * Helper: Batch fetch records by ID from a table, returns a map of id -> record
 */
async function batchFetchRecordsById(tableIdOrName: string, ids: string[]): Promise<Record<string, any>> {
  if (!ids.length) return {};
  // Handle both record IDs and table IDs
  const filterByFormula = `OR(${ids.map(id =>
    id.startsWith('rec') ?
      `RECORD_ID()='${id}'` :
      `{ID}='${id}'`
  ).join(',')})`;
  try {
    const { fetchRecords } = await import('./rest-api');
    const response = await fetchRecords(tableIdOrName, { filterByFormula });
    // Map by id and also by any ID field if present
    const map: Record<string, any> = {};
    for (const rec of response.records) {
      map[rec.id] = rec;
      if (rec.fields.ID) {
        map[rec.fields.ID] = rec;
      }
    }
    return map;
  } catch (err) {
    console.error(`[batchFetchRecordsById] Error fetching records from ${tableIdOrName}:`, err);
    return {};
  }
}

/**
 * Batch version of dynamicDeserialize: processes all records, batches linked record fetches, and resolves links using pre-fetched data.
 */
export async function batchDynamicDeserialize(tableName: string, records: any[]): Promise<any[]> {
  if (!records || records.length === 0) return [];
  // Defensive: flatten all records
  const flats = records.map(record => {
    if (record && typeof record === 'object' && record.fields && typeof record.fields === 'object') {
      return { id: record.id, ...record.fields };
    } else {
      return { ...record };
    }
  });
  // Get field map for the table
  const fieldMap = await getDynamicFieldMap(tableName);
  // Collect all linked record IDs per linked table
  const linkedTableToIds: Record<string, Set<string>> = {};
  for (const flat of flats) {
    for (const [field, value] of Object.entries(flat)) {
      const fieldDef = fieldMap[field];
      if (fieldDef && fieldDef.type === 'lookup' && fieldDef.options) {
        const linkedTableId = fieldDef.options?.linkedTableId;
        if (linkedTableId && Array.isArray(value) && value.length > 0 && value[0]?.startsWith?.('rec')) {
          if (!linkedTableToIds[linkedTableId]) linkedTableToIds[linkedTableId] = new Set();
          value.forEach((id: string) => linkedTableToIds[linkedTableId].add(id));
        }
      }
    }
  }
  // Batch fetch all needed linked records per table
  const linkedTableData: Record<string, Record<string, any>> = {};
  for (const [linkedTableId, idsSet] of Object.entries(linkedTableToIds)) {
    const ids = Array.from(idsSet);
    linkedTableData[linkedTableId] = await batchFetchRecordsById(linkedTableId, ids);
  }
  // Now, for each record, deserialize using the pre-fetched linked data
  return await Promise.all(
    records.map((record, idx) => dynamicDeserialize(tableName, record, linkedTableData))
  );
}

// Only one export for dynamicDeserialize, with optional preFetchedLinkedData param
export async function dynamicDeserialize(tableName: string, record: any, preFetchedLinkedData?: Record<string, Record<string, any>>) {
  console.log('[dynamicDeserialize] called with:', tableName, record, 'stack:', new Error().stack);
  // Defensive: If record has a 'fields' property, flatten it
  let flat: any;
  if (record && typeof record === 'object' && record.fields && typeof record.fields === 'object') {
    flat = { id: record.id, ...record.fields };
  } else {
    flat = { ...record };
  }

  // Get field map for the table
  const fieldMap = await getDynamicFieldMap(tableName);
  // Prepare a copy to mutate
  const result: any = { ...flat };

  // For each field, check if it's a lookup of a linked record
  for (const [field, value] of Object.entries(flat)) {
    const fieldDef = fieldMap[field];
    if (fieldDef && fieldDef.type === 'lookup' && fieldDef.options) {
      // Check if this lookup is of a linked record
      const linkedTableId = fieldDef.options?.linkedTableId;
      if (linkedTableId && Array.isArray(value) && value.length > 0 && value[0]?.startsWith?.('rec')) {
        // Use pre-fetched data if available
        let linkedRecords: Record<string, any> | undefined = undefined;
        if (preFetchedLinkedData && preFetchedLinkedData[linkedTableId]) {
          linkedRecords = preFetchedLinkedData[linkedTableId];
        } else {
          linkedRecords = await batchFetchRecordsById(linkedTableId, value);
        }
        // Replace the array of IDs with the array of full objects
        result[field] = value.map((id: string) => linkedRecords && linkedRecords[id] ? { id: linkedRecords[id].id, ...linkedRecords[id].fields } : { id });
      }
      // else: lookup of primitive, leave as-is
    }
  }
  return result;
}
